import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { Form, Formik } from 'formik';
import { useEffect, useState } from 'react';
import * as bd from 'react-basic-design';
import {
    LicenseCodeDTO,
    useInitDownloadableLicenseCodesApi,
    useInitDownloadableLicenseCodesByProductIdApi,
    requestDownloadLicenseCodesApi,
} from '../../../api/license-code-api';
import { ProductDTO } from '../../../api/product-api';
import SvgClose from '../../../assets/icons/Close';
import SvgDownload from '../../../assets/icons/Download';
import SvgExcel from '../../../assets/icons/Excel';
import SvgInfo from '../../../assets/icons/Info';
import { BasicModal } from '../../../components/basic/basic-modal';
import { InitFailed } from '../../../components/basic/init-failed';
import { Loading } from '../../../components/basic/loading';
import { N } from '../../../components/basic/n';
import { T, translate } from '../../../components/basic/text';
import { notify } from '../../../components/basic/notify';
import { LoadingButton } from '../../../components/loading-button';

export function LicenseCodeDownloadExcelModal({
    show,
    products,
    onHide,
}: {
    show: boolean;
    products: ProductDTO[];
    onHide: () => void;
}) {
    const initDownloadableLicenseCodes = useInitDownloadableLicenseCodesApi();
    const [selectedProductId, setSelectedProductId] = useState(0);

    const { result, error, initialized, initializing } = initDownloadableLicenseCodes;

    const columns = [
        {
            Header: <T>product</T>,
            accessor: 'productId',
            Cell: ({ row }: { row: any }) => (
                <bd.Flex align="center" gap={2}>
                    <span>{products.find(x => x.id === row.productId)?.name}</span>
                    <small>
                        (<N>{row.count}</N>)
                    </small>
                </bd.Flex>
            ),
        },
        {
            Header: <T>download-for-print</T>,
            accessor: 'count',
            width: 150,
            Cell: ({ row }: { row: any }) => (
                <bd.Button
                    variant="text"
                    size="sm"
                    disabled={row.count === 0}
                    onClick={() => setSelectedProductId(row.productId)}
                >
                    <SvgDownload />
                </bd.Button>
            ),
        },
    ];

    const clear = () => {
        setSelectedProductId(0);
        onHide();
        initDownloadableLicenseCodes.reset();
    };

    useEffect(() => {
        if (!show || initialized || initializing) return;
        initDownloadableLicenseCodes.get();
    }, [show, initialized, initializing, initDownloadableLicenseCodes]);

    if (error instanceof Error)
        return (
            <InitFailed ex={error} modal show={show} onHide={clear} hideGoBack onRetry={() => {}} />
        );
    if (!initialized || initializing) return <Loading show={show} modal onHide={clear} />;

    const { liceenseCodes } = result;

    return (
        <Formik initialValues={{ productId: 0 }} onSubmit={() => {}}>
            {formik => (
                <Form>
                    <BasicModal
                        show={show}
                        onHide={clear}
                        title={
                            <bd.Flex align="center" content="between" className="p-0 m-0">
                                <T>download-license-codes</T>
                                <bd.Button size="sm" variant="icon" onClick={clear}>
                                    <SvgClose />
                                </bd.Button>
                            </bd.Flex>
                        }
                        size="lg"
                        depth="3"
                    >
                        <bd.BasicTable className="mx-n3" columns={columns} data={liceenseCodes}>
                            <T as="div" className="nothing-found">
                                nothing-found
                            </T>
                        </bd.BasicTable>

                        {selectedProductId > 0 && (
                            <LicenseCodeDownloadExcelContentModal
                                onHide={() => {
                                    setSelectedProductId(0);
                                    initDownloadableLicenseCodes.reset();
                                }}
                                productId={selectedProductId}
                                productName={
                                    products.find(x => x.id === selectedProductId)?.name ?? ''
                                }
                            />
                        )}
                    </BasicModal>
                </Form>
            )}
        </Formik>
    );
}

const LicenseCodeDownloadExcelContentModal = ({
    onHide,
    productId,
    productName,
}: {
    onHide: () => void;
    productId: number;
    productName: string;
}) => {
    const initDownloadableLicenseCodes = useInitDownloadableLicenseCodesByProductIdApi();
    const [loading, setLoading] = useState(false);
    const [productCount, setProductCount] = useState(0);
    const [requestCount, setRequestCount] = useState(0);
    const [hasError, setHasError] = useState(false);

    const { result, error, initialized, initializing } = initDownloadableLicenseCodes;

    useEffect(() => {
        if (initialized || initializing || productId <= 0) return;
        initDownloadableLicenseCodes.get({
            productId,
        });
    }, [initialized, initializing, initDownloadableLicenseCodes, productId]);

    useEffect(() => {
        if (result && !!result.liceenseCodes.length) {
            setProductCount(result.liceenseCodes.find(x => x.productId === productId)?.count ?? 0);
        }
    }, [result, productId]);

    useEffect(() => {
        if (requestCount > (productCount ?? 0) || productCount === 0 || requestCount === 0) {
            setHasError(true);
        } else {
            setHasError(false);
        }
    }, [requestCount, productCount]);

    const clear = () => {
        initDownloadableLicenseCodes.reset();
        onHide();
    };

    if (error instanceof Error)
        return <InitFailed ex={error} modal onHide={clear} hideGoBack onRetry={() => {}} />;
    if (!initialized || initializing) return <Loading show={true} modal onHide={clear} />;

    const handleDownloadRequest = () => {
        setLoading(true);
        requestDownloadLicenseCodesApi({
            productId,
            count: requestCount,
        })
            .then(res => {
                var downloadedLicenseCodes = res.licenseCodes;
                handleDownloadExcel(downloadedLicenseCodes);
                clear();
                notify.success(translate('license-code-downloaded-successfully'));
            })
            .catch(notify.error)
            .finally(() => setLoading(false));
    };

    const handleDownloadExcel = async (licenses: LicenseCodeDTO[]) => {
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet(productName);

        worksheet.columns = [{ header: 'License Code', key: 'code', width: 30 }];
        licenses.forEach(l => worksheet.addRow(l));

        worksheet.getRow(1).getCell(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
        worksheet.getRow(1).getCell(1).fill = {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FF28A745' },
        };
        worksheet.getRow(1).getCell(1).alignment = { horizontal: 'center', vertical: 'middle' };

        const buffer = await workbook.xlsx.writeBuffer();
        saveAs(new Blob([buffer]), `${productName}.xlsx`);
    };
    return (
        <BasicModal
            depth="n"
            size="lg"
            show={true}
            onHide={clear}
            title={
                <bd.Flex className="w-100" align="center" content="between">
                    <bd.Flex align="center" gap={2}>
                        <T>download-license-codes</T>
                        <small className="small">({productName})</small>
                    </bd.Flex>
                    <bd.Button variant="icon" onClick={clear}>
                        <SvgClose />
                    </bd.Button>
                </bd.Flex>
            }
        >
            <bd.Flex vertical className="p-2">
                <bd.Flex align="center" gap={2} className="p-3 bg-light rounded">
                    <SvgInfo className="text-primary" />
                    <T>total-available</T>
                    <N>{productCount}</N>
                    <T>item</T>
                </bd.Flex>

                <bd.Flex vertical gap={2}>
                    <T variant="subtitle" className="text-muted">
                        download-count
                    </T>

                    <bd.Flex align="center" gap={2}>
                        <bd.TextField
                            type="number"
                            error={hasError ? 'error' : ''}
                            value={requestCount}
                            onChange={e => setRequestCount(parseInt(e.target.value))}
                            min={1}
                            max={productCount}
                            className="w-100"
                            placeholder="0"
                        />
                        <LoadingButton
                            variant="outline"
                            className="text-success border-success"
                            onClick={handleDownloadRequest}
                            disabled={requestCount === 0 || hasError}
                            icon={<SvgExcel />}
                            label={<T>download</T>}
                            loading={loading}
                        />
                    </bd.Flex>
                </bd.Flex>
            </bd.Flex>
        </BasicModal>
    );
};
